import { useState, useRef, useEffect } from "preact/hooks";
import LogLine from "./LogLine";
import type { LogEntry } from "../state";
import { usernameColorClass } from "../state";
import { getUserAvatar } from "../avatars";
import { BrutalButton } from "./BrutalUI";
import type { AvatarBitmap } from "../avatars";

const ACTION_TYPES = new Set([
  "slot_created",
  "slot_deleted",
  "slot_description_updated",
  "reaction_set",
  "reaction_cleared",
  "tag_created",
  "tag_added_to_slot",
  "tag_removed_from_slot",
]);

function isActionType(type: string): boolean {
  return ACTION_TYPES.has(type);
}

export function groupLogMessages(
  entries: LogEntry[]
): Array<LogEntry | LogEntry[]> {
  const result: Array<LogEntry | LogEntry[]> = [];
  let group: LogEntry[] = [];
  let groupUser: string | null = null;

  for (const entry of entries) {
    const isAction = isActionType(entry.type);
    const user = entry.username ?? "";

    if (isAction && groupUser === user && group.length > 0) {
      group.push(entry);
    } else {
      if (group.length === 1) {
        result.push(group[0]);
      } else if (group.length > 1) {
        result.push(group);
      }
      group = [];
      groupUser = null;

      if (isAction) {
        group = [entry];
        groupUser = user;
      } else {
        result.push(entry);
      }
    }
  }

  if (group.length === 1) {
    result.push(group[0]);
  } else if (group.length > 1) {
    result.push(group);
  }

  return result;
}

interface LogGroupProps {
  entries: LogEntry[];
  slug?: string;
  username?: string;
  reactionsByMessage?: Record<number, Record<string, number>>;
  reactionsByUserByMessage?: Record<number, Record<string, string[]>>;
  usedEmojis?: string[];
  avatarsByUsername?: Record<string, AvatarBitmap>;
}

export default function LogGroup({
  entries,
  slug,
  username,
  reactionsByMessage = {},
  reactionsByUserByMessage = {},
  usedEmojis = [],
  avatarsByUsername = {},
}: LogGroupProps) {
  const [expanded, setExpanded] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const first = entries[0];
  const displayName = first.username || "System";

  useEffect(() => {
    if (expanded && contentRef.current) {
      contentRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [expanded]);

  return (
    <div ref={contentRef} className="overflow-visible">
      <BrutalButton
        type="button"
        variant="secondary"
        onClick={() => setExpanded((e) => !e)}
        className="w-full justify-start text-sm px-3 py-2 bg-orange-50 dark:bg-gray-900 hover:bg-orange-100 dark:hover:bg-gray-800 gap-2"
      >
        <span
          className={`inline-block transition-transform ${expanded ? "rotate-90" : ""}`}
          aria-hidden
        >
          ▶
        </span>
        <span className="text-xs opacity-80 flex-1">
          <span className={`font-semibold ${usernameColorClass(displayName)}`}>
            {displayName}
          </span>{" "}
          · {entries.length} actions
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {first.created_at}
        </span>
      </BrutalButton>
      {expanded ? (
        <div className="flex flex-col gap-1 p-2 border-2 border-black dark:border-gray-500 mt-1 bg-white dark:bg-gray-950">
          {entries.map((entry) => (
            <LogLine
              key={entry.id}
              entry={entry}
              slug={slug}
              username={username}
              reactionCounts={
                entry.type === "chat_message_posted"
                  ? reactionsByMessage[entry.id]
                  : undefined
              }
              currentUserEmojis={
                entry.type === "chat_message_posted" && username
                  ? (() => {
                      const v = reactionsByUserByMessage[entry.id]?.[username];
                      return Array.isArray(v) ? v : v ? [v] : [];
                    })()
                  : undefined
              }
              reactionsByUser={
                entry.type === "chat_message_posted"
                  ? reactionsByUserByMessage[entry.id]
                  : undefined
              }
              usedEmojis={usedEmojis}
              avatar={getUserAvatar(entry.username ?? "", avatarsByUsername)}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
